import { useState, useEffect, useMemo } from "react";
import {
  FaFire,
  FaCalendarDays,
  FaNoteSticky,
  FaMedal,
  FaChevronDown,
  FaListCheck,
} from "react-icons/fa6";
import { useTranslation } from "react-i18next";

const StatisticsBlocks = ({ allMoods, dayStreak, achievements }) => {
  const { t } = useTranslation();

  // 'week', 'month', 'all'.
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [targetDate, setTargetDate] = useState(new Date());

  // Стан пікера
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (isDatePickerOpen) {
      setPickerYear(targetDate.getFullYear());
    }
  }, [isDatePickerOpen, targetDate]);

  // --- ЛОГІКА ДАТ ---
  const monthNames = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2000, i, 1);
      return d.toLocaleString("default", { month: "long" });
    });
  }, []);

  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear;

    if (allMoods && allMoods.length > 0) {
      const firstMoodDate = new Date(allMoods[0].date * 1000);
      startYear = firstMoodDate.getFullYear();
    }
    if (startYear > currentYear) startYear = currentYear;

    const years = [];
    for (let y = startYear; y <= currentYear; y++) {
      years.push(y);
    }
    return years;
  };

  const isMonthDisabled = (year, monthIndex) => {
    const now = new Date();
    if (year === now.getFullYear() && monthIndex > now.getMonth()) return true;
    if (year > now.getFullYear()) return true;
    return false;
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(targetDate);
    newDate.setFullYear(pickerYear);
    newDate.setMonth(monthIndex);
    setTargetDate(newDate);
    setSelectedPeriod("month");
    setIsDatePickerOpen(false);
  };

  // --- ЛОГІКА ФІЛЬТРАЦІЇ ---

  const filteredMoods = useMemo(() => {
    if (!allMoods || allMoods.length === 0) return [];
    const now = new Date();

    return allMoods.filter(({ date }) => {
      const moodDate = new Date(date * 1000);

      if (selectedPeriod === "week") {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        return moodDate >= weekAgo;
      }

      if (selectedPeriod === "month") {
        return (
          moodDate.getMonth() === targetDate.getMonth() &&
          moodDate.getFullYear() === targetDate.getFullYear()
        );
      }
      return true;
    });
  }, [allMoods, selectedPeriod, targetDate]);

  // --- СТАТИСТИКА (ВІДФІЛЬТРОВАНА) ---
  const totalMoods = filteredMoods.length;

  const avgMood =
    totalMoods > 0
      ? filteredMoods.reduce((acc, item) => acc + Number(item.mood), 0) /
        totalMoods
      : 0;
  const avgMoodRounded = avgMood.toFixed(1);

  const totalNotes = filteredMoods.reduce(
    (count, item) => (item.note ? count + 1 : count),
    0
  );

  // Підрахунок Follow Ups (опитувань)
  const totalFollowUps = filteredMoods.reduce(
    (count, item) => (item.follow_up ? count + 1 : count),
    0
  );

  // --- СТАТИСТИКА (ГЛОБАЛЬНА) ---
  let achievementsCount = 0;
  if (achievements) {
    achievementsCount = Object.values(achievements).filter((v) => v).length;
  }

  if (!allMoods) return null;

  return (
    <div className="space-y-6 mt-6 relative">
      {/* 1. БЛОК ФІЛЬТРІВ */}
      <div className="flex flex-col gap-3 relative z-30">
        <div className="flex justify-between items-center px-1">
          <p className="text-xl font-bold text-white tracking-wide">
            {t("statistics")}
          </p>
        </div>

        <div className="bg-slate-900/80 p-1 rounded-xl flex gap-1 border border-white/5">
          <button
            onClick={() => {
              setSelectedPeriod("week");
              setIsDatePickerOpen(false);
            }}
            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-300 ${
              selectedPeriod === "week"
                ? "bg-slate-700 text-white shadow-md"
                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
            }`}
          >
            {t("periods.week")}
          </button>

          <button
            onClick={() => {
              if (selectedPeriod === "month") {
                setIsDatePickerOpen(!isDatePickerOpen);
              } else {
                setSelectedPeriod("month");
              }
            }}
            className={`flex-[1.5] py-2 px-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-300 flex items-center justify-center gap-1 outline-0 ${
              selectedPeriod === "month"
                ? "bg-slate-700 text-emerald-400 shadow-md border border-emerald-500/30"
                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
            }`}
          >
            <span className="truncate">
              {selectedPeriod === "month"
                ? `${targetDate.toLocaleString("default", {
                    month: "short",
                  })} ${targetDate.getFullYear()}`
                : t("periods.month")}
            </span>
            {selectedPeriod === "month" && (
              <FaChevronDown
                className={`text-[10px] transition-transform duration-300 ${
                  isDatePickerOpen ? "rotate-180" : ""
                }`}
              />
            )}
          </button>

          <button
            onClick={() => {
              setSelectedPeriod("all");
              setIsDatePickerOpen(false);
            }}
            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-300 ${
              selectedPeriod === "all"
                ? "bg-slate-700 text-white shadow-md"
                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
            }`}
          >
            {t("periods.all")}
          </button>
        </div>
      </div>

      {/* OVERLAY DATE PICKER */}
      <div
        className={`absolute inset-x-0 top-20 bottom-0 bg-slate-900/95 backdrop-blur-md z-40 rounded-2xl border border-slate-700 transition-all duration-300 ease-in-out flex flex-col overflow-hidden shadow-2xl ${
          isDatePickerOpen
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex flex-1 h-full">
          <div className="w-1/3 border-r border-slate-700 overflow-y-auto hidden-scrollbar py-2 bg-slate-800/50">
            <div className="flex flex-col gap-1 px-2">
              {getAvailableYears().map((year) => (
                <button
                  key={year}
                  onClick={() => setPickerYear(year)}
                  className={`py-3 text-sm font-bold rounded-lg transition-colors ${
                    pickerYear === year
                      ? "bg-emerald-600 text-white shadow-md scale-105"
                      : "text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
          <div className="w-2/3 overflow-y-auto hidden-scrollbar py-2">
            <div className="grid grid-cols-2 gap-2 px-2">
              {monthNames.map((mName, index) => {
                const disabled = isMonthDisabled(pickerYear, index);
                const isSelected =
                  pickerYear === targetDate.getFullYear() &&
                  index === targetDate.getMonth();

                return (
                  <button
                    key={index}
                    disabled={disabled}
                    onClick={() => handleMonthSelect(index)}
                    className={`py-3 text-sm font-semibold rounded-lg capitalize transition-all ${
                      isSelected
                        ? "bg-slate-700 text-emerald-400 border border-emerald-500/50 shadow-inner"
                        : disabled
                        ? "text-slate-700 cursor-not-allowed opacity-50"
                        : "text-slate-200 hover:bg-slate-800 active:scale-95 bg-slate-800/30"
                    }`}
                  >
                    {mName}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 2. ВІДФІЛЬТРОВАНА СТАТИСТИКА */}
      <div className="grid grid-cols-2 gap-3 transition-opacity duration-300">
        {/* Average Mood (Full Width) */}
        <div className="col-span-2 bg-slate-800 border border-slate-700 rounded-2xl p-5 flex items-center justify-between relative overflow-hidden">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="flex items-center gap-5 z-10">
            <div className="relative">
              <img
                src={`/${Math.round(avgMoodRounded) || 0}.webp`}
                className="w-20 h-20 object-contain drop-shadow-md transform transition-transform hover:scale-110"
                alt={t("averageMood")}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">
                {t("averageMood")}
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-black text-white">
                  {avgMoodRounded}
                </span>
                <span className="text-lg text-slate-500 font-medium">/ 5</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marks Count */}
        <div className="col-span-2 bg-slate-800 border border-slate-700 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-400 uppercase">
              {t("marks")}
            </span>
            <p className="text-3xl font-black text-white">
              {filteredMoods?.length || 0}
            </p>
          </div>
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <FaCalendarDays className="text-emerald-400 text-2xl" />
          </div>
        </div>

        {/* Notes Count */}
        <div className="col-span-1 bg-slate-800 border border-slate-700 rounded-2xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase">
              {t("notes")}
            </span>
            <FaNoteSticky className="text-yellow-400 text-lg" />
          </div>
          <p className="text-3xl font-black text-white">{totalNotes}</p>
        </div>

        {/* Follow Ups Count (Full Width) */}
        <div className="col-span-1 bg-slate-800 border border-slate-700 rounded-2xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase">
              {t("poll") || "Follow Ups"}
            </span>
            <FaListCheck className="text-purple-400 text-lg" />
          </div>
          <p className="text-3xl font-black text-white">{totalFollowUps}</p>
        </div>
      </div>

      {/* 3. ГЛОБАЛЬНА СТАТИСТИКА (Внизу) */}
      <div className="pt-4 border-t border-white/10">
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-1">
          {t("totalProgress") || "Global Stats"}
        </p>

        <div className="grid grid-cols-2 gap-3">
          {/* Day Streak */}
          <div className="col-span-1 rounded-2xl p-4 flex flex-col justify-between h-32 relative overflow-hidden bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move shadow-lg">
            <div className="absolute top-0 right-0 p-3 opacity-20">
              <FaFire className="w-16 h-16 text-white rotate-12" />
            </div>
            <div className="bg-white/20 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm z-10">
              <FaFire className="w-5 h-5 text-white" />
            </div>
            <div className="z-10">
              <p className="text-4xl font-black text-white leading-none mb-1">
                {dayStreak}
              </p>
              <p className="text-xs font-bold text-white/90 uppercase tracking-wide">
                {t("dayStrike")}
              </p>
            </div>
          </div>

          {/* Achievements */}
          <div className="col-span-1 rounded-2xl p-4 flex flex-col justify-between h-32 bg-slate-800 border border-slate-700 relative overflow-hidden">
            <div className="absolute -bottom-2 -right-2 opacity-10">
              <FaMedal className="w-20 h-20 text-yellow-500 -rotate-12" />
            </div>
            <div className="bg-yellow-500/20 w-10 h-10 rounded-full flex items-center justify-center border border-yellow-500/30">
              <FaMedal className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-4xl font-black text-white leading-none mb-1">
                {achievementsCount}
              </p>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                {t("achievements")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsBlocks;
